import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, Copy, Disc3, FileSpreadsheet, KeyRound, Layers, ListMusic, Search, Users, UsersRound } from "lucide-react";
import Layout from "@/components/Layout";
import { EmptyState, LoadingPanel, StatCard } from "@/components/Bits";
import { Pill } from "@/components/Music";
import ViewToggle, { useViewMode } from "@/components/ViewToggle";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTenant } from "@/context/TenantContext";
import { api, apiError, formatDate, formatDateTime } from "@/lib/api";
import { useRemembered } from "@/lib/useRemembered";
import { cn } from "@/lib/utils";

const inputCls = "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";

/** The owner's home: every workspace at a glance. */
export default function PlatformOverview() {
  const { setTenantId } = useTenant();
  const navigate = useNavigate();
  const [data, setData] = useRemembered("platform-overview", { owner: true });
  const [mode, setMode] = useViewMode("platform", "grid");

  useEffect(() => {
    api
      .get("/platform/overview")
      .then(({ data: d }) => setData(d))
      .catch((e) => toast.error(apiError(e)));
  }, [setData]);

  const open = (w, path = "/overview") => {
    setTenantId(w.tenant_id);
    navigate(path);
  };

  if (!data)
    return (
      <Layout owner title="Platform overview">
        <LoadingPanel rows={6} />
      </Layout>
    );

  const t = data.totals;
  const firstRun = t.tracks === 0 && data.workspaces.length === 1;

  return (
    <Layout
      owner
      title="Platform overview"
      subtitle={`${t.workspaces} workspace${t.workspaces === 1 ? "" : "s"} · ${t.logins} people with a login`}
      action={
        <Button asChild className="bg-brand hover:bg-brand-hover">
          <Link to="/workspaces?new=1">
            <Layers className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">New workspace</span>
          </Link>
        </Button>
      }
    >
      {firstRun && (
        <section className="panel panel-pad mb-4 flex items-center gap-4 flex-wrap border-brand-ring">
          <div className="w-11 h-11 rounded-xl bg-brand-light flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5 text-brand" />
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="font-display text-[18px] font-semibold">Next: bring in your spreadsheet</div>
            <p className="text-[13px] text-muted2 mt-0.5">Open {data.workspaces[0].name} and import your .xlsx. Each tab becomes the catalog, a writer or the production board.</p>
          </div>
          <Button className="bg-brand hover:bg-brand-hover" onClick={() => open(data.workspaces[0], "/import")}>
            Import into {data.workspaces[0].name} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </section>
      )}

      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-5">
        <StatCard label="Workspaces" value={t.workspaces} sub={`${t.active} active`} icon={Layers} />
        <StatCard label="Tracks" value={t.tracks} sub="Across every workspace" icon={ListMusic} />
        <StatCard label="Writers" value={t.writers} sub={`${t.logins} logins in total`} icon={Users} />
        <StatCard label="Need fixing" value={t.problems} tone={t.problems ? "warn" : "good"} sub={`${t.bmi_open} not yet at BMI`} icon={AlertTriangle} />
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-[18px] font-semibold">Workspaces</h2>
        <ViewToggle mode={mode} onChange={setMode} />
      </div>

      {mode === "grid" ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.workspaces.map((w) => {
            const pct = w.tracks ? Math.round((w.bmi_registered / w.tracks) * 100) : 0;
            return (
              <article key={w.tenant_id} className={cn("panel p-5 flex flex-col gap-4", !w.active && "opacity-70")}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-brand flex items-center justify-center shrink-0">
                    <Disc3 className="w-5 h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[17px] font-semibold truncate">{w.name}</div>
                    <div className="font-mono text-[11px] text-muted2">
                      {w.publisher_pro}
                      {w.publisher_ipi ? ` · IPI ${w.publisher_ipi}` : ""}
                    </div>
                  </div>
                  {!w.active && <Pill tone="warn">Suspended</Pill>}
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    ["Tracks", w.tracks],
                    ["Writers", w.writers],
                    ["Logins", w.logins],
                  ].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-surface py-2">
                      <div className="font-display text-[20px] font-semibold">{v}</div>
                      <div className="eyebrow text-[9.5px]">{l}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex justify-between text-[12px] text-muted2 mb-1.5">
                    <span>{w.problems ? `${w.problems} tracks need fixing` : "No blocking problems"}</span>
                    <span>{pct}% at BMI</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-surface">
                    <div className="h-1.5 rounded-full bg-sec-pro" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-line mt-auto">
                  <span className="text-[12px] text-muted2">{w.last_activity ? `Last edit ${formatDate(w.last_activity)}` : "No tracks yet"}</span>
                  <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => open(w)}>
                    Open <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["Workspace", "Tracks", "Released", "Writers", "Logins", "At BMI", "Need fixing", "Last edit", ""].map((h) => (
                    <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.workspaces.map((w) => (
                  <tr key={w.tenant_id} className={cn("t-row", !w.active && "opacity-70")}>
                    <td className="t-cell">
                      <div className="font-semibold">{w.name}</div>
                      <div className="font-mono text-[11px] text-muted2">{w.publisher_ipi ? `IPI ${w.publisher_ipi}` : w.publisher_pro}</div>
                    </td>
                    <td className="t-cell font-mono">{w.tracks}</td>
                    <td className="t-cell font-mono">{w.released}</td>
                    <td className="t-cell font-mono">{w.writers}</td>
                    <td className="t-cell font-mono">{w.logins}</td>
                    <td className="t-cell font-mono">{w.bmi_registered}</td>
                    <td className="t-cell">{w.problems ? <Pill tone="warn">{w.problems}</Pill> : <span className="text-muted2">—</span>}</td>
                    <td className="t-cell text-[12.5px]">{w.last_activity ? formatDate(w.last_activity) : "—"}</td>
                    <td className="t-cell text-right">
                      <Button size="sm" variant="outline" onClick={() => open(w)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}

const ROLE_TONE = { owner: "brand", admin: "brand", staff: "muted", writer: "good" };

/** Everyone who can sign in, in every workspace. */
export function PlatformPeople() {
  const [rows, setRows] = useRemembered("platform-people", { owner: true });
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [link, setLink] = useState(null);

  useEffect(() => {
    api
      .get("/platform/people")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(apiError(e)));
  }, [setRows]);

  const resetLink = async (u) => {
    try {
      const { data } = await api.post(`/platform/people/${u.user_id}/reset-link`);
      setLink(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const list = (rows || []).filter((u) => (!role || u.role === role) && (!q || `${u.name} ${u.email} ${u.tenant_name}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <Layout owner title="People" subtitle="Everyone with a login, across all workspaces">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <label className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, email, workspace…" aria-label="Search people" className="h-9 w-[260px] pl-9 pr-3 rounded-lg border border-line bg-card text-[13px] outline-none focus:border-brand-ring" />
        </label>
        {["", "admin", "staff", "writer"].map((r) => (
          <button
            key={r || "all"}
            onClick={() => setRole(r)}
            aria-pressed={role === r}
            className={cn("h-8 px-3 rounded-full text-[12.5px] font-semibold border capitalize", role === r ? "bg-brand text-white border-brand" : "bg-card border-line hover:border-brand-ring")}
          >
            {r || "Everyone"}
          </button>
        ))}
      </div>
      {!rows ? (
        <LoadingPanel />
      ) : list.length === 0 ? (
        <div className="panel">
          <EmptyState icon={UsersRound} title="No one here yet" body="Add people from a workspace's Team page, or give a writer a login from Writers." />
        </div>
      ) : (
        <div className="panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="bg-surface/70 border-b border-line">
                  {["Person", "Role", "Workspace", "Last signed in", ""].map((h) => (
                    <th key={h} className="t-head text-left px-4 py-2.5 font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.user_id} className="t-row">
                    <td className="t-cell">
                      <div className="font-semibold">{u.name}</div>
                      <div className="text-[12px] text-muted2">{u.email}</div>
                    </td>
                    <td className="t-cell">
                      <Pill tone={ROLE_TONE[u.role]}>{u.role === "owner" ? "Platform owner" : u.role}</Pill>
                      {u.must_change_password && <div className="text-[11.5px] text-gold mt-1">Hasn't set a password yet</div>}
                    </td>
                    <td className="t-cell text-[13px]">{u.tenant_name || <span className="text-muted2">All</span>}</td>
                    <td className="t-cell text-[12.5px]">{u.last_login_at ? formatDateTime(u.last_login_at) : "Never"}</td>
                    <td className="t-cell text-right">
                      {u.role !== "owner" && (
                        <button onClick={() => resetLink(u)} title="Password reset link" aria-label="Password reset link" className="p-1.5 rounded-lg text-muted2 hover:bg-brand-light hover:text-brand">
                          <KeyRound className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Dialog open={!!link} onOpenChange={(v) => !v && setLink(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Reset link for {link?.email}</DialogTitle>
          </DialogHeader>
          <p className="text-[13px] text-muted2">Send them this link. It works once and expires in {link?.expires_in_minutes} minutes.</p>
          <div className="flex gap-2">
            <input readOnly value={link?.link || ""} className={`${inputCls} font-mono text-[12px]`} aria-label="Reset link" />
            <Button
              variant="outline"
              onClick={() => {
                navigator.clipboard?.writeText(link.link);
                toast.success("Copied");
              }}
            >
              <Copy className="w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/** App-wide settings: the name and wording on the sign-in screen. */
export { PlatformSettings } from "@/pages/Appearance";
